/**
 * Rally Lab (spec sections 36, 37).
 *
 * Two rules shape this screen.
 *
 * There is no universal rally score. A recommendation is always *for a role* —
 * Bear offense, PvP garrison, fortress attack — because a strong Bear lead and
 * a strong garrison are different players (section 37). The event type and side
 * pick the role; the server refuses to answer when the pairing has no role.
 *
 * And `troopComposition` is nullable, with no fallback in the schema. Null
 * means no recorded result supports a composition, and the correct rendering is
 * to say so. Substituting a community-guess ratio here would be exactly the
 * failure this system exists to prevent (sections 29, 74): a number nobody
 * measured, presented with the same authority as one we did. Neither locale
 * prints one.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { RallyLabResult, RallyRecommendRequest, RallyRecommendResponse } from '@kso/schemas';
import { useAllianceMembers, useRallyRecommend } from '../../api/queries';
import { useCoreContext } from '../../lib/coreAlliance';
import {
  Badge,
  Card,
  Notice,
  PageHeader,
  ReasonList,
  Value,
  WarningList,
} from '../../components/ui/primitives';
import { EmptyState, ErrorState, LoadingBlock, QueryBoundary } from '../../components/ui/states';
import { ConfidenceBadge, InferenceMark, SampleCount } from '../../components/inference/Inference';
import { formatDateTime } from '../../lib/format';
import { eventTypeLabel, rallyRoleLabel } from '../../lib/enumLabels';
import { useT } from '../../i18n';

/**
 * Only the event types that actually involve a rally. Mobilization and KvK are
 * deliberately absent — there is no rally to compose for them, and offering the
 * option would imply the model has something to say.
 */
const RALLY_EVENT_TYPES: Array<RallyRecommendRequest['eventType']> = [
  'BEAR_TRAP',
  'ALLIANCE_CHAMPIONSHIP',
  'SWORDLAND',
  'SANCTUARY',
  'FORTRESS',
  'CASTLE',
  'VIKING_VENGEANCE',
  'TRI_ALLIANCE_CLASH',
];

export function RallyLabPage() {
  const t = useT();
  const core = useCoreContext();
  const members = useAllianceMembers(core.allianceId ?? undefined);
  const recommend = useRallyRecommend();

  const [eventType, setEventType] = useState<RallyRecommendRequest['eventType']>('BEAR_TRAP');
  const [side, setSide] = useState<'ATTACK' | 'DEFENSE'>('ATTACK');
  const [roster, setRoster] = useState<Set<string>>(new Set());
  const [candidateLead, setCandidateLead] = useState('');
  const [joinerCount, setJoinerCount] = useState(5);

  const memberById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members.data?.members ?? []) map.set(member.id, member.name ?? member.id);
    return map;
  }, [members.data]);

  if (core.isPending) return <LoadingBlock label={t.common.loadingAlliance} />;
  if (core.error) return <ErrorState error={core.error} />;
  if (!core.allianceId) {
    return (
      <>
        <PageHeader title={t.rallyLab.title} />
        <EmptyState
          title={t.common.noCoreAllianceTitle}
          body={t.rallyLab.noCoreAllianceBody}
          action={
            <Link className="btn btn--sm" to="/data/sync">
              {t.common.openSyncStatus}
            </Link>
          }
        />
      </>
    );
  }

  function submit() {
    if (roster.size === 0) return;
    recommend.mutate({
      eventType,
      side,
      roster: [...roster],
      joinerCount,
      ...(candidateLead ? { candidateLeadPlayerId: candidateLead } : {}),
    });
  }

  return (
    <>
      <PageHeader title={t.rallyLab.title} subtitle={t.rallyLab.subtitle} />

      <div className="stack">
        <Notice severity="INFO">{t.rallyLab.perRoleNotice}</Notice>

        <Card title={t.rallyLab.whatAreYouBuilding}>
          <div className="row" style={{ gap: 16, alignItems: 'flex-end' }}>
            <label className="row" style={{ gap: 6 }}>
              <span className="muted small">{t.rallyLab.operation}</span>
              <select
                value={eventType}
                onChange={(event) =>
                  setEventType(event.target.value as RallyRecommendRequest['eventType'])
                }
              >
                {RALLY_EVENT_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {eventTypeLabel(t, option)}
                  </option>
                ))}
              </select>
            </label>

            <label className="row" style={{ gap: 6 }}>
              <span className="muted small">{t.rallyLab.side}</span>
              <select
                value={side}
                onChange={(event) => setSide(event.target.value as 'ATTACK' | 'DEFENSE')}
              >
                <option value="ATTACK">{t.rallyLab.attack}</option>
                <option value="DEFENSE">{t.rallyLab.defense}</option>
              </select>
            </label>

            <label className="row" style={{ gap: 6 }}>
              <span className="muted small">{t.rallyLab.joiners}</span>
              <input
                type="number"
                min={1}
                max={50}
                value={joinerCount}
                onChange={(event) => setJoinerCount(Number(event.target.value))}
                style={{ width: 70 }}
              />
            </label>

            <label className="row" style={{ gap: 6 }}>
              <span className="muted small">{t.rallyLab.leadThisRally}</span>
              <select
                value={candidateLead}
                onChange={(event) => setCandidateLead(event.target.value)}
                style={{ minWidth: 170 }}
              >
                <option value="">{t.rallyLab.letModelChoose}</option>
                {[...roster].map((playerId) => (
                  <option key={playerId} value={playerId}>
                    {memberById.get(playerId) ?? playerId}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </Card>

        <QueryBoundary
          query={members}
          loadingLabel={t.common.loadingRoster}
          isEmpty={(data) => data.members.length === 0}
          empty={<EmptyState title={t.rallyLab.noRosterTitle} body={t.rallyLab.noRosterBody} />}
        >
          {(data) => (
            <Card
              title={t.rallyLab.availableTitle(roster.size)}
              actions={
                <div className="row" style={{ gap: 4 }}>
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => setRoster(new Set(data.members.map((member) => member.id)))}
                  >
                    {t.common.selectAll}
                  </button>
                  <button type="button" className="btn btn--sm" onClick={() => setRoster(new Set())}>
                    {t.common.clear}
                  </button>
                </div>
              }
            >
              <div className="row" style={{ gap: 6 }}>
                {[...data.members]
                  .sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id))
                  .map((member) => {
                    const selected = roster.has(member.id);
                    return (
                      <button
                        key={member.id}
                        type="button"
                        className={selected ? 'btn btn--sm btn--primary' : 'btn btn--sm'}
                        onClick={() =>
                          setRoster((current) => {
                            const next = new Set(current);
                            if (selected) next.delete(member.id);
                            else next.add(member.id);
                            return next;
                          })
                        }
                      >
                        {member.name ?? member.id}
                      </button>
                    );
                  })}
              </div>

              <div className="row" style={{ marginTop: 14 }}>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={submit}
                  disabled={roster.size === 0 || recommend.isPending}
                >
                  {recommend.isPending ? t.rallyLab.composing : t.rallyLab.recommend}
                </button>
                {roster.size === 0 ? (
                  <span className="muted small">{t.rallyLab.pickAvailableFirst}</span>
                ) : null}
              </div>
            </Card>
          )}
        </QueryBoundary>

        {recommend.error ? <ErrorState error={recommend.error} /> : null}
        {recommend.data ? (
          <RecommendationView response={recommend.data} memberById={memberById} />
        ) : null}
      </div>
    </>
  );
}

function RecommendationView({
  response,
  memberById,
}: {
  response: RallyRecommendResponse;
  memberById: Map<string, string>;
}) {
  const t = useT();
  const { result } = response;

  if (result.role === null) {
    return <EmptyState title={t.rallyLab.noRoleTitle} body={t.rallyLab.noRoleBody} />;
  }

  const name = (playerId: string, fallback: string | null) =>
    fallback ?? memberById.get(playerId) ?? playerId;

  return (
    <div className="stack">
      <Card
        title={
          <span className="row">
            {t.rallyLab.recommendedFor(rallyRoleLabel(t, result.role))}{' '}
            <InferenceMark modelVersion={response.modelVersion} />
          </span>
        }
        actions={
          <ConfidenceBadge level={response.confidenceLevel} confidence={response.confidence} />
        }
      >
        <div className="grid grid--halves">
          <div>
            <h3 className="muted small">{t.rallyLab.lead}</h3>
            {result.recommendedLead ? (
              <p style={{ margin: '4px 0' }}>
                <Link to={`/players/${encodeURIComponent(result.recommendedLead.playerId)}`}>
                  <strong>
                    {name(result.recommendedLead.playerId, result.recommendedLead.playerName)}
                  </strong>
                </Link>
                {result.recommendedLead.score === null ? (
                  <span className="absent small"> — {t.common.notScored}</span>
                ) : (
                  <span className="muted small"> — {result.recommendedLead.score.toFixed(0)}</span>
                )}
              </p>
            ) : (
              <p className="absent small" style={{ margin: '4px 0' }}>
                {t.rallyLab.noLead}
              </p>
            )}

            <h3 className="muted small" style={{ marginTop: 10 }}>
              {t.rallyLab.backupLead}
            </h3>
            {result.backupLead ? (
              <p style={{ margin: '4px 0' }}>
                {name(result.backupLead.playerId, result.backupLead.playerName)}
              </p>
            ) : (
              <p className="absent small" style={{ margin: '4px 0' }}>
                {t.rallyLab.noBackup}
              </p>
            )}

            <h3 className="muted small" style={{ marginTop: 10 }}>
              {t.rallyLab.leadHeroes}
            </h3>
            {result.recommendedLeaderHeroes.length === 0 ? (
              <p className="absent small" style={{ margin: '4px 0' }}>
                {t.rallyLab.noHeroRecommendation}
              </p>
            ) : (
              <p style={{ margin: '4px 0' }} className="row">
                {result.recommendedLeaderHeroes.map((hero) => (
                  <span key={hero.heroId} className={hero.heroName ? undefined : 'mono'}>
                    {hero.heroName ?? t.rallyLab.rawHeroId(hero.heroId)}
                  </span>
                ))}
              </p>
            )}
          </div>

          <TroopComposition composition={result.troopComposition} />
        </div>

        <div style={{ marginTop: 12 }}>
          <h3 className="muted small">{t.common.why}</h3>
          <ReasonList reasons={response.reasons} />
        </div>

        <div style={{ marginTop: 10 }}>
          <WarningList warnings={response.warnings} />
        </div>

        <p className="faint small" style={{ marginTop: 10, marginBottom: 0 }}>
          {t.rallyLab.computedFrom(
            formatDateTime(response.sourceDataAt) ?? t.bearAnalysis.unrecordedTime,
          )}
          {response.ruleVersion ? t.rallyLab.rules(response.ruleVersion) : ''}
          {response.recommendationRunId
            ? t.rallyLab.run(response.recommendationRunId)
            : t.rallyLab.notPersisted}
        </p>
      </Card>

      <Card title={t.rallyLab.joinersTitle}>
        {result.recommendedJoiners.length === 0 ? (
          <p className="absent small" style={{ margin: 0 }}>
            {t.rallyLab.noJoiners}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="num">{t.rallyLab.joinerColumns.rank}</th>
                  <th>{t.common.player}</th>
                  <th className="num">{t.rallyLab.joinerColumns.score}</th>
                  <th>{t.rallyLab.joinerColumns.captainHero}</th>
                </tr>
              </thead>
              <tbody>
                {result.recommendedJoiners.map((joiner, index) => (
                  <tr key={joiner.playerId}>
                    <td className="num faint">{index + 1}</td>
                    <td>
                      <Link to={`/players/${encodeURIComponent(joiner.playerId)}`}>
                        <Value absent={t.common.unnamedPlayer}>
                          {joiner.playerName ?? memberById.get(joiner.playerId) ?? null}
                        </Value>
                      </Link>
                    </td>
                    <td className="num">
                      {joiner.score === null ? (
                        <span className="absent">{t.common.notScored}</span>
                      ) : (
                        joiner.score.toFixed(0)
                      )}
                    </td>
                    <td>
                      {joiner.captainHeroId === null ? (
                        <span className="absent">{t.rallyLab.noRecommendation}</span>
                      ) : (
                        <span className={joiner.captainHeroName ? undefined : 'mono'}>
                          {joiner.captainHeroName ?? t.rallyLab.rawHeroId(joiner.captainHeroId)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <MissingData missing={result.missingData} />
    </div>
  );
}

/**
 * Null composition is the interesting case, and it is the default one early on.
 *
 * The schema has no fallback ratio and neither does this component. "0/20/80"
 * is a widely repeated community guess; printing it here because we had nothing
 * else would turn a rumour into this system's recommendation.
 */
function TroopComposition({ composition }: { composition: RallyLabResult['troopComposition'] }) {
  const t = useT();

  if (!composition) {
    return (
      <div>
        <h3 className="muted small">{t.rallyLab.troopComposition}</h3>
        <p className="absent" style={{ margin: '4px 0' }}>
          {t.rallyLab.noComposition}
        </p>
        <p className="muted small" style={{ margin: 0 }}>
          {t.rallyLab.noCompositionBody}
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="muted small">{t.rallyLab.troopComposition}</h3>
      {/* The composition label is a ratio like "0/20/80" — the same in both
          locales, and read straight from the recorded result. */}
      <p style={{ margin: '4px 0', fontSize: 20, fontWeight: 600 }}>{composition.label}</p>
      <div className="row" style={{ gap: 8 }}>
        <Badge tone="neutral" title={t.rallyLab.normalizedTitle}>
          {t.rallyLab.normalized(composition.normalized.toFixed(2))}
        </Badge>
        <SampleCount samples={composition.samples} noun={t.inference.nouns.observedRallies} />
      </div>
    </div>
  );
}

function MissingData({ missing }: { missing: readonly string[] }) {
  const t = useT();
  if (missing.length === 0) return null;
  return (
    <Card title={t.rallyLab.missingTitle}>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t.rallyLab.missingBlurb}
      </p>
      <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
        {missing.map((entry) => (
          <li key={entry}>{entry}</li>
        ))}
      </ul>
    </Card>
  );
}
